import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  Alert,
  DatePicker,
  Select,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  Timeline,
  Steps,
  Divider,
  InputNumber,
  Descriptions,
  Spin,
  Empty,
  Radio
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CalendarOutlined,
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  DashboardOutlined,
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  TeamOutlined,
  ToolOutlined,
  BankOutlined,
  StockOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs;
const { Option } = Select;

interface ReportData {
  id: string;
  name: string;
  type: string;
  module: string;
  description: string;
  lastGenerated: string;
  status: string;
  size: string;
  format: string;
}

interface DashboardMetrics {
  totalRevenue: number;
  revenueGrowth: number;
  totalOrders: number;
  ordersGrowth: number;
  totalCustomers: number;
  customersGrowth: number;
  totalEmployees: number;
  employeesGrowth: number;
  manufacturingEfficiency: number;
  qualityScore: number;
  inventoryValue: number;
  cashFlow: number;
}

interface ChartData {
  period: string;
  sales: number;
  orders: number;
  customers: number;
  revenue: number;
  expenses: number;
  profit: number;
}

const ReportsOverview: React.FC = () => {
  const location = useLocation();
  
  // Function to get active tab from route
  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    if (path.includes('/reports/sales')) return 'sales';
    if (path.includes('/reports/financial')) return 'financial';
    if (path.includes('/reports/inventory')) return 'inventory';
    if (path.includes('/reports/hr')) return 'hr';
    if (path.includes('/reports/manufacturing')) return 'manufacturing';
    if (path.includes('/reports/crm')) return 'crm';
    if (path.includes('/reports/custom')) return 'custom';
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [reports, setReports] = useState<ReportData[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalRevenue: 0,
    revenueGrowth: 0,
    totalOrders: 0,
    ordersGrowth: 0,
    totalCustomers: 0,
    customersGrowth: 0,
    totalEmployees: 0,
    employeesGrowth: 0,
    manufacturingEfficiency: 0,
    qualityScore: 0,
    inventoryValue: 0,
    cashFlow: 0
  });
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [selectedModule, setSelectedModule] = useState('all');
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  // Load mock data
  useEffect(() => {
    setLoading(true);
    
    // Mock dashboard metrics
    setMetrics({
      totalRevenue: 1847250,
      revenueGrowth: 15.8,
      totalOrders: 342,
      ordersGrowth: 12.3,
      totalCustomers: 156,
      customersGrowth: 8.7,
      totalEmployees: 48,
      employeesGrowth: 6.2,
      manufacturingEfficiency: 87.5,
      qualityScore: 95.8,
      inventoryValue: 892340,
      cashFlow: 284750
    });

    // Mock chart data for last 12 months
    const mockChartData: ChartData[] = [];
    for (let i = 11; i >= 0; i--) {
      const date = dayjs().subtract(i, 'month');
      mockChartData.push({
        period: date.format('MMM YYYY'),
        sales: Math.floor(Math.random() * 200000) + 100000,
        orders: Math.floor(Math.random() * 50) + 20,
        customers: Math.floor(Math.random() * 20) + 10,
        revenue: Math.floor(Math.random() * 250000) + 150000,
        expenses: Math.floor(Math.random() * 180000) + 100000,
        profit: Math.floor(Math.random() * 80000) + 40000
      });
    }
    setChartData(mockChartData);

    // Mock reports data
    setReports([
      {
        id: '1',
        name: 'Monthly Sales Report',
        type: 'Sales Analytics',
        module: 'Sales',
        description: 'Comprehensive sales performance analysis',
        lastGenerated: dayjs().subtract(1, 'day').format('YYYY-MM-DD HH:mm'),
        status: 'completed',
        size: '2.4 MB',
        format: 'PDF'
      },
      {
        id: '2',
        name: 'Financial Statement',
        type: 'Financial Report',
        module: 'Accounting',
        description: 'Profit & Loss, Balance Sheet, Cash Flow',
        lastGenerated: dayjs().subtract(2, 'hours').format('YYYY-MM-DD HH:mm'),
        status: 'completed',
        size: '1.8 MB',
        format: 'Excel'
      },
      {
        id: '3',
        name: 'Employee Performance',
        type: 'HR Analytics',
        module: 'HR',
        description: 'Employee KPIs and performance metrics',
        lastGenerated: dayjs().subtract(3, 'days').format('YYYY-MM-DD HH:mm'),
        status: 'completed',
        size: '3.2 MB',
        format: 'PDF'
      },
      {
        id: '4',
        name: 'Manufacturing Efficiency',
        type: 'Production Report',
        module: 'Manufacturing',
        description: 'Production line efficiency and quality metrics',
        lastGenerated: dayjs().subtract(5, 'hours').format('YYYY-MM-DD HH:mm'),
        status: 'processing',
        size: '1.5 MB',
        format: 'Excel'
      },
      {
        id: '5',
        name: 'Inventory Valuation',
        type: 'Inventory Report',
        module: 'Inventory',
        description: 'Stock levels, valuation, and movement analysis',
        lastGenerated: dayjs().subtract(1, 'week').format('YYYY-MM-DD HH:mm'),
        status: 'completed',
        size: '2.1 MB',
        format: 'PDF'
      },
      {
        id: '6',
        name: 'Customer Analytics',
        type: 'CRM Report',
        module: 'CRM',
        description: 'Customer acquisition, retention, and lifetime value',
        lastGenerated: dayjs().subtract(2, 'days').format('YYYY-MM-DD HH:mm'),
        status: 'completed',
        size: '2.8 MB',
        format: 'Excel'
      }
    ]);

    setLoading(false);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'processing';
      case 'failed': return 'error';
      case 'scheduled': return 'warning';
      default: return 'default';
    }
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'Sales': return <ShoppingCartOutlined />;
      case 'Accounting': return <BankOutlined />;
      case 'HR': return <TeamOutlined />;
      case 'Manufacturing': return <ToolOutlined />;
      case 'Inventory': return <StockOutlined />;
      case 'CRM': return <UserOutlined />;
      default: return <BarChartOutlined />;
    }
  };

  const handleGenerateReport = async (values: any) => {
    try {
      setLoading(true);
      // API call would go here
      message.success('Report generation started successfully');
      setIsReportModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = (reportId: string, format: string) => {
    message.success(`Exporting report to ${format} format...`);
    // Export logic would go here
  };

  const reportsColumns: ColumnsType<ReportData> = [
    {
      title: 'Report Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {getModuleIcon(record.module)}
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      render: (module) => <Tag color="blue">{module}</Tag>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge
          status={getStatusColor(status) as any}
          text={status.charAt(0).toUpperCase() + status.slice(1)}
        />
      )
    },
    {
      title: 'Last Generated',
      dataIndex: 'lastGenerated',
      key: 'lastGenerated',
      render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm')
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Download PDF">
            <Button
              type="text"
              icon={<FilePdfOutlined />}
              onClick={() => handleExportReport(record.id, 'PDF')}
            />
          </Tooltip>
          <Tooltip title="Download Excel">
            <Button
              type="text"
              icon={<FileExcelOutlined />}
              onClick={() => handleExportReport(record.id, 'Excel')}
            />
          </Tooltip>
          <Tooltip title="Print">
            <Button
              type="text"
              icon={<PrinterOutlined />}
              onClick={() => message.info('Opening print preview...')}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const renderDashboard = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Total Revenue"
                  value={metrics.totalRevenue}
                  precision={0}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<DollarOutlined />}
                  suffix={
                    <Space>
                      <RiseOutlined style={{ color: '#3f8600' }} />
                      <Text style={{ color: '#3f8600', fontSize: '14px' }}>
                        +{metrics.revenueGrowth}%
                      </Text>
                    </Space>
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Total Orders"
                  value={metrics.totalOrders}
                  valueStyle={{ color: '#1890ff' }}
                  prefix={<ShoppingCartOutlined />}
                  suffix={
                    <Space>
                      <RiseOutlined style={{ color: '#3f8600' }} />
                      <Text style={{ color: '#3f8600', fontSize: '14px' }}>
                        +{metrics.ordersGrowth}%
                      </Text>
                    </Space>
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Total Customers"
                  value={metrics.totalCustomers}
                  valueStyle={{ color: '#722ed1' }}
                  prefix={<UserOutlined />}
                  suffix={
                    <Space>
                      <RiseOutlined style={{ color: '#3f8600' }} />
                      <Text style={{ color: '#3f8600', fontSize: '14px' }}>
                        +{metrics.customersGrowth}%
                      </Text>
                    </Space>
                  }
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Total Employees"
                  value={metrics.totalEmployees}
                  valueStyle={{ color: '#fa8c16' }}
                  prefix={<TeamOutlined />}
                  suffix={
                    <Space>
                      <RiseOutlined style={{ color: '#3f8600' }} />
                      <Text style={{ color: '#3f8600', fontSize: '14px' }}>
                        +{metrics.employeesGrowth}%
                      </Text>
                    </Space>
                  }
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Manufacturing KPIs" extra={<ToolOutlined />}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Efficiency"
                  value={metrics.manufacturingEfficiency}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: '#3f8600' }}
                />
                <Progress 
                  percent={metrics.manufacturingEfficiency} 
                  size="small" 
                  status="active"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Quality Score"
                  value={metrics.qualityScore}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: '#1890ff' }}
                />
                <Progress 
                  percent={metrics.qualityScore} 
                  size="small" 
                  status="active"
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Financial Overview" extra={<BankOutlined />}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Inventory Value"
                  value={metrics.inventoryValue}
                  precision={0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Cash Flow"
                  value={metrics.cashFlow}
                  precision={0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Revenue Trend (Last 12 Months)" extra={<LineChartOutlined />}>
            <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Text type="secondary">
                Chart visualization would be rendered here using a charting library like Chart.js or Recharts
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderModuleReports = (module: string) => {
    const moduleReports = reports.filter(report => 
      module === 'all' || report.module.toLowerCase() === module.toLowerCase()
    );

    return (
      <div>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Space>
              <Search
                placeholder={`Search ${module} reports...`}
                style={{ width: 300 }}
                onSearch={(value) => console.log('Search:', value)}
              />
              <Select
                defaultValue="all"
                style={{ width: 120 }}
                onChange={setSelectedModule}
              >
                <Option value="all">All Modules</Option>
                <Option value="sales">Sales</Option>
                <Option value="accounting">Accounting</Option>
                <Option value="hr">HR</Option>
                <Option value="manufacturing">Manufacturing</Option>
                <Option value="inventory">Inventory</Option>
                <Option value="crm">CRM</Option>
              </Select>
              <RangePicker
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                format="YYYY-MM-DD"
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsReportModalVisible(true)}>
                Generate Report
              </Button>
            </Space>
          </Col>
        </Row>

        <Card>
          <Table
            columns={reportsColumns}
            dataSource={moduleReports}
            rowKey="id"
            loading={loading}
            pagination={{
              //pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: [10, 20, 50, 100, 500],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} reports`
            }}
          />
        </Card>
      </div>
    );
  };

  const renderReportModal = () => (
    <Modal
      title="Generate New Report"
      open={isReportModalVisible}
      onOk={() => form.submit()}
      onCancel={() => setIsReportModalVisible(false)}
      width={600}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleGenerateReport}
      >
        <Form.Item
          name="reportName"
          label="Report Name"
          rules={[{ required: true, message: 'Please enter report name' }]}
        >
          <Input placeholder="Enter report name" />
        </Form.Item>

        <Form.Item
          name="module"
          label="Module"
          rules={[{ required: true, message: 'Please select module' }]}
        >
          <Select placeholder="Select module">
            <Option value="sales">Sales</Option>
            <Option value="accounting">Accounting</Option>
            <Option value="hr">HR</Option>
            <Option value="manufacturing">Manufacturing</Option>
            <Option value="inventory">Inventory</Option>
            <Option value="crm">CRM</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="reportType"
          label="Report Type"
          rules={[{ required: true, message: 'Please select report type' }]}
        >
          <Select placeholder="Select report type">
            <Option value="summary">Summary Report</Option>
            <Option value="detailed">Detailed Analysis</Option>
            <Option value="comparison">Comparison Report</Option>
            <Option value="trend">Trend Analysis</Option>
            <Option value="custom">Custom Report</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="dateRange"
          label="Date Range"
          rules={[{ required: true, message: 'Please select date range' }]}
        >
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="format"
          label="Output Format"
          rules={[{ required: true, message: 'Please select format' }]}
        >
          <Radio.Group>
            <Radio value="pdf">PDF</Radio>
            <Radio value="excel">Excel</Radio>
            <Radio value="csv">CSV</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
        >
          <Input.TextArea
            rows={3}
            placeholder="Enter report description (optional)"
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          Reports & Analytics
        </Title>
        <Text type="secondary">
          Comprehensive reporting and analytics across all business modules
        </Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        tabBarExtraContent={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
              Refresh
            </Button>
            <Button type="primary" icon={<DownloadOutlined />}>
              Export All
            </Button>
          </Space>
        }
      >
        <TabPane tab={<span><DashboardOutlined />Dashboard</span>} key="dashboard">
          {renderDashboard()}
        </TabPane>

        <TabPane tab={<span><ShoppingCartOutlined />Sales Reports</span>} key="sales">
          {renderModuleReports('Sales')}
        </TabPane>

        <TabPane tab={<span><BankOutlined />Financial Reports</span>} key="financial">
          {renderModuleReports('Accounting')}
        </TabPane>

        <TabPane tab={<span><StockOutlined />Inventory Reports</span>} key="inventory">
          {renderModuleReports('Inventory')}
        </TabPane>

        <TabPane tab={<span><TeamOutlined />HR Reports</span>} key="hr">
          {renderModuleReports('HR')}
        </TabPane>

        <TabPane tab={<span><ToolOutlined />Manufacturing Reports</span>} key="manufacturing">
          {renderModuleReports('Manufacturing')}
        </TabPane>

        <TabPane tab={<span><UserOutlined />CRM Reports</span>} key="crm">
          {renderModuleReports('CRM')}
        </TabPane>

        <TabPane tab={<span><BarChartOutlined />Custom Reports</span>} key="custom">
          <Card>
            <Empty
              description="Custom report builder coming soon"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        </TabPane>
      </Tabs>

      {renderReportModal()}
    </div>
  );
};

export default ReportsOverview; 